import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  IDashboardTopChannelsParams,
  ITopChannelsInfo,
  queryDashboardTopChannels,
} from '~/src/dash/api/dashboards/home';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  smallTableRowsPerPageOptions,
} from '~/src/dash/components/tables/ListViewTable/common';
import { Row, Rows, Text, usePublicaTranslation } from '~/src/dash/publicaUI';
import { extractRouteParams, getRoutePath, RouteChannelExisting } from '~/src/dash/router/routes';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isSamsungNAPublisher } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IDashboardSubTable } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const formatTopChannelsResponseIntoLVTRows = (
  sites: ITopChannelsInfo[],
  cfg: IDashboardSubTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  sites.forEach((site) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = site[responseKey as keyof ITopChannelsInfo];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

const getActiveColumnKeys = (graphID: number, publisherID: string) => {
  const keys = ['Revenue', 'Impressions', 'CPM', 'CPMPerSecond', 'RenderRate'];
  if (graphID === 48) {
    keys.push('EndpointRequests', 'RenderedPodFillRate', 'PodFillRate', 'PodClearRate');
    if (!isSamsungNAPublisher(publisherID)) {
      keys.push('FillRate');
    }
  }
  return keys;
};

interface ITopChannelsTableProps {
  params: IDashboardTopChannelsParams;
  columns: IListViewTableColumns;
  rowsPerPage: RowsPerPage;
  setRowsPerPage: React.Dispatch<React.SetStateAction<-1 | 5 | 10 | 25 | 50 | 100>>;
  initialLoadStart: number;
}

export const TopChannelsTable = ({
  columns,
  params,
  rowsPerPage,
  setRowsPerPage,
  initialLoadStart,
}: ITopChannelsTableProps) => {
  const { session, user } = useBaseStore();
  const { t } = usePublicaTranslation();
  const isInitialized = useRef(false);

  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [query, setQuery] = useState({} as IDashboardTopChannelsParams);
  const [responseData, setResponseData] = useState<ITopChannelsInfo[]>([]);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState('revenue');
  const [page, setPage] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const readAuthorization = IS_AUTHORIZED(['read_channel'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_channel'], user.user);
  const isSuperUser = hasRoleSuperUser(user.user);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  useEffect(() => {
    if (!isEmpty(params)) {
      unstable_batchedUpdates(() => {
        setQuery(params);
        setPage(0);
      });
    }
  }, [params]);

  useEffect(() => {
    if (!isEmpty(query)) {
      getData();
    }
  }, [query, order, orderBy, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const getData = () => {
    setLoadState(LoadingStateLoading);

    // update query object
    query.statFilter = orderBy;
    query.statOrder = order;
    query.limit = rowsPerPage;
    query.offset = page * rowsPerPage;

    queryDashboardTopChannels(session.publisher.ID, query)
      .then((result) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setTotalRowCount(result.TotalRowCount);
          setResponseData(result.Data || []);
        });
        if (!isInitialized.current) {
          track(
            'Dash Overview - Initial Top Channels Table Load Time',
            { isSuperUser, initialLoadTime: moment().unix() - initialLoadStart },
            user.user,
          );
          isInitialized.current = true;
        }
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setSnackbarError(true);
          setSnackbarMessage(t('BiddersSnackbarErrorRetrievingTopChannels'));
        });
      });
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  // get url string from a channel id
  const getChannelRoute = (id: string): string => {
    return getRoutePath(RouteChannelExisting, extractRouteParams({ session, user }), { ch: id });
  };

  const rows = formatTopChannelsResponseIntoLVTRows(responseData, { columns });

  return (
    <>
      <Rows>
        <Row noHorizontalPadding smallVerticalPadding>
          <Text bold tKey='TopChannels'>
            Top Channels
          </Text>
        </Row>
        <Row noPadding>
          <ListViewTable
            activeColumnKeys={getActiveColumnKeys(query.graph_id, session.publisher.ID)}
            columns={columns}
            getItemSettingsRoute={getChannelRoute}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOnClickColumnHeader={handleOnClickColumnHeader}
            isReadAuthorized={readAuthorization.isAuthorized}
            isSuperUser={isSuperUser}
            isWriteAuthorized={writeAuthorization.isAuthorized}
            loadState={loadState}
            order={order}
            orderBy={orderBy}
            page={page}
            rows={rows}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={smallTableRowsPerPageOptions}
            totalRowCount={totalRowCount}
            className={'topchannelsdashboard'}
            disableCurrencyConversion
          />
        </Row>
      </Rows>
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
